// Does a document belong to the applicant? Deterministic check over its OCR
// text (no AI): the applicant's cédula / NUIP, other identity numbers the
// document names, and the applicant's name. Mirror of the backend
// gfunctions/extension/applicants/_shared/identityCheck.js — keep both in sync.
//
// Results (status):
//   MATCH       — their number is there, or their name is and no other number is
//   ID_DIFFERS  — their name is there but only a different identity number
//                 (e.g. an old tarjeta de identidad on a bachiller diploma)
//   MISMATCH    — another person's identity number, and neither their number
//                 nor their name
//   UNKNOWN     — nothing to decide on (no text, no identity data found)

const normalize = (s) => String(s == null ? "" : s)
  .normalize("NFD").replace(/[̀-ͯ]/g, "")
  .toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
const digitsOf = (s) => String(s == null ? "" : s).replace(/\D/g, "");

// Words that sit inside names without identifying anyone.
const NAME_PARTICLES = new Set(["de", "del", "la", "las", "los", "y", "e", "san", "da", "van"]);
const nameTokens = (s) => normalize(s).split(" ").filter((t) => t.length >= 3 && !NAME_PARTICLES.has(t));

// Numbers introduced as a PERSON's identity document. Short markers are matched
// in capitals only ("C.C.", "T.I.", "C.E."): lowercase "cc" / "ti" are ordinary
// Spanish. A company NIT is never an identity number here.
const LONG_MARKERS = /(c[ée]dula(?:\s+de\s+(?:ciudadan[ií]a|extranjer[ií]a))?|tarjeta\s+de\s+identidad|documento\s+de\s+identidad|identificad[oa]\s+con|n[uú]mero\s+[uú]nico\s+de\s+identificaci[oó]n|nuip)[^0-9\n]{0,40}?(\d[\d., -]{3,16}\d)/gi;
const SHORT_MARKERS = /\b(C\.?\s?C|T\.?\s?I|C\.?\s?E)\b\.?[^0-9\n]{0,15}?(\d[\d., -]{3,16}\d)/g;
const MIN_ID_DIGITS = 5;
const MAX_ID_DIGITS = 12;

export function identityNumbers(text) {
  const out = new Set();
  for (const re of [LONG_MARKERS, SHORT_MARKERS]) {
    re.lastIndex = 0;
    let m;
    while ((m = re.exec(text))) {
      const n = digitsOf(m[2]);
      if (n.length >= MIN_ID_DIGITS && n.length <= MAX_ID_DIGITS) out.add(n);
    }
  }
  return [...out];
}

// Every digit run in the text, separators ("1.000.111.222", "1 000 111 222")
// removed — a RUT prints the cédula as the NIT, a certificate may print it
// without any marker; any exact occurrence counts. Each hyphen-separated part
// counts on its own too: a NIT carries its check digit ("1000111222-5").
function allNumbers(text) {
  const runs = String(text).match(/\d[\d., -]*\d|\d/g) || [];
  const out = new Set();
  for (const run of runs) {
    out.add(digitsOf(run));
    run.split("-").forEach((part) => out.add(digitsOf(part)));
  }
  return out;
}

// { status, idFound, nameFound, otherIds }. `person` = { nuip, firstName, lastName }.
export function checkIdentity(text, person) {
  const raw = String(text || "");
  const nuip = digitsOf(person && person.nuip);
  const unknown = { status: "UNKNOWN", idFound: false, nameFound: false, otherIds: [] };
  if (!raw.trim() || !person) return unknown;

  const idFound = !!nuip && allNumbers(raw).has(nuip);
  const otherIds = identityNumbers(raw).filter((n) => n !== nuip);

  // Their name: at least one given name AND one surname, as whole words.
  const words = new Set(normalize(raw).split(" "));
  const first = nameTokens(person.firstName);
  const last = nameTokens(person.lastName);
  const nameFound = first.some((t) => words.has(t)) && last.some((t) => words.has(t));

  let status = "UNKNOWN";
  if (idFound) status = "MATCH";
  else if (nameFound) status = otherIds.length ? "ID_DIFFERS" : "MATCH";
  else if (otherIds.length) status = "MISMATCH";
  return { status, idFound, nameFound, otherIds: otherIds.slice(0, 5) };
}

// What an AI reader said about the holder ({ holderName, holderId }) against
// the applicant. Same statuses; UNKNOWN when the reader found no holder.
export function compareHolder(holder, person) {
  const id = digitsOf(holder && holder.holderId);
  const name = String((holder && holder.holderName) || "");
  if (!person || (!id && !name.trim())) return { status: "UNKNOWN" };
  const nuip = digitsOf(person.nuip);
  const idFound = !!id && id === nuip;
  const words = new Set(nameTokens(name));
  const nameFound = nameTokens(person.firstName).some((t) => words.has(t))
    && nameTokens(person.lastName).some((t) => words.has(t));
  if (idFound) return { status: "MATCH" };
  if (nameFound) return { status: id ? "ID_DIFFERS" : "MATCH" };
  return { status: id || words.size ? "MISMATCH" : "UNKNOWN" };
}

// Statuses that deserve a warning.
export const IDENTITY_WARNINGS = new Set(["MISMATCH", "ID_DIFFERS"]);

// A file's check: the one intake stored, else computed from its OCR text (files
// uploaded before the check existed).
export function fileIdentity(file, person) {
  if (file && file.identity && file.identity.status) return file.identity;
  if (file && file.documentText && person) return checkIdentity(file.documentText, person);
  return null;
}

// The applicant-level verdict over all their current files — mirror of the
// backend screeningSummary.
//   FLAGGED    — some document seems someone else's (MISMATCH or ID_DIFFERS)
//   INCOMPLETE — nothing flagged, but some document couldn't be verified
//   PASSED     — every document matches the applicant
//   null       — no documents to screen
export function screeningSummary(files, person) {
  let flagged = 0;
  let unverified = 0;
  let matched = 0;
  for (const f of Array.isArray(files) ? files : []) {
    if (!f) continue;
    const status = (f.identity && f.identity.status)
      || (f.documentText ? checkIdentity(f.documentText, person).status : "UNKNOWN");
    if (status === "MISMATCH" || status === "ID_DIFFERS") flagged += 1;
    else if (status === "MATCH") matched += 1;
    else unverified += 1;
  }
  const total = flagged + unverified + matched;
  if (!total) return { status: null, flagged: 0, unverified: 0, matched: 0 };
  const status = flagged ? "FLAGGED" : unverified ? "INCOMPLETE" : "PASSED";
  return { status, flagged, unverified, matched };
}

const filesOf = (docMap) => Object.values(docMap || {})
  .flatMap((d) => (d && Array.isArray(d.files) ? d.files : []));

// An applicant's verdict. With their postulaciones at hand (the review page)
// it is computed live over everything; otherwise (the list) the one intake
// stored, or — for applicants not saved since the check existed — one computed
// from their profile documents.
export function applicantScreening(applicant, applications = null) {
  if (!applicant) return null;
  if (!applications && applicant.screening && applicant.screening.status !== undefined) return applicant.screening;
  const files = [
    ...filesOf(applicant.profileDocuments),
    ...(applications || []).flatMap((p) => filesOf(p && p.documents)),
  ];
  return screeningSummary(files, applicant);
}
